import * as React from 'react'

import { cn } from '~/client/src/shared/utils/cn'

import { Checkbox } from '../shadcn-ui/checkbox'
import { CardListGroupToggles } from './CardListGroupToggles'
import { ICardListItem, ICardListState } from './types'

interface ICardListSummaryBarSelectAll {
  checked: boolean
  indeterminate?: boolean
  disabled?: boolean
  onToggle: () => void
  ariaLabel?: string
}

interface IProps<T extends ICardListItem> {
  /** Multi-select only: select-all over the listed items. */
  selectAll?: ICardListSummaryBarSelectAll
  /** Renders the compact collapse controls once the list has groups. */
  listState?: ICardListState<T>
  /** Scopes the toggles' "more" menu to an in-box portal (e.g. inside a popover). */
  portalSelector?: string
  /** The count / selection label. */
  children: React.ReactNode
  /** After the label, e.g. bulk actions or a spacer for overlaid header buttons. */
  trailing?: React.ReactNode
  className?: string
}

/**
 * The list summary bar (Figma "Modal Summary Bar") above a CardList: 36px, 16px left /
 * 8px right inset, 8px between items, body-md muted label, input-soft divider.
 * The compact toggles' 24px buttons carry a 4px icon inset, so `-mx-1` keeps the
 * icons themselves 8px from their neighbours.
 */
export function CardListSummaryBar<T extends ICardListItem>({
  selectAll,
  listState,
  portalSelector,
  children,
  trailing,
  className,
}: IProps<T>): React.ReactElement {
  const hasToggles = !!listState && listState.groups.length > 0

  return (
    <div
      className={cn(
        'tw:flex tw:items-center tw:gap-2 tw:h-9 tw:w-full tw:shrink-0 tw:pl-4 tw:pr-2 tw:whitespace-nowrap tw:bg-background tw:text-body-md tw:text-muted-foreground tw:border-0 tw:border-b tw:border-solid tw:border-input-soft',
        className,
      )}
    >
      {selectAll && (
        <Checkbox
          checked={selectAll.checked}
          indeterminate={selectAll.indeterminate}
          disabled={selectAll.disabled}
          onCheckedChange={() => selectAll.onToggle()}
          aria-label={selectAll.ariaLabel}
        />
      )}
      {hasToggles && (
        <CardListGroupToggles
          compact={true}
          className="tw:-mx-1"
          listState={listState}
          portalSelector={portalSelector}
        />
      )}
      <span className="tw:min-w-0 tw:truncate">{children}</span>
      {trailing}
    </div>
  )
}
